/**
 * ComradeVexAI — pure behaviour tree for "Comrade Vex" Stage 6 boss.
 * No Phaser imports — fully testable in Node.js.
 *
 * Crimson Cadre ringleader. Armed with a banner-staff and a megaphone.
 *
 * Attacks:
 *   banner_sweep    — wide melee arc requiring close quarters
 *   megaphone_stun  — cone AoE stun that fires at any range
 *
 * Round-robin: 0 → banner_sweep, 1 → megaphone_stun
 *
 * Summon waves of cadre-agitator reinforcements (each once):
 *   Wave 1 at 66% HP
 *   Wave 2 at 33% HP
 *
 * Phase 2 (HP ≤ 33%): faster cooldowns.
 */
import { type EnemyAIInput, ENGAGE_RANGE } from '@/systems/EnemyAI';

// ── Range constants ───────────────────────────────────────────────────────────
/** Banner-staff sweep requires close quarters. */
export const BANNER_SWEEP_RANGE = 120;

// ── Timing constants ─────────────────────────────────────────────────────────
/** Telegraph frames before the banner sweep connects. */
export const BANNER_WINDUP_FRAMES = 20;
/** Telegraph frames before the megaphone cone fires. */
export const MEGAPHONE_WINDUP_FRAMES = 24;
/** Retreat frames after an attack. */
export const BACKSTEP_FRAMES = 22;
/** Frames Vex raises the banner to rally troops. */
export const SUMMON_FRAMES = 28;

// ── HP thresholds ─────────────────────────────────────────────────────────────
/** HP fraction at which the first cadre-agitator wave is summoned (once). */
export const SUMMON_WAVE_1_THRESHOLD = 0.66;
/** HP fraction at which the second cadre-agitator wave is summoned (once). */
export const SUMMON_WAVE_2_THRESHOLD = 0.33;
/** HP fraction at which phase 2 (faster cooldowns) activates. */
export const PHASE_2_THRESHOLD = 0.33;

// ── Speed / cooldown tuning ───────────────────────────────────────────────────
const BASE_COOLDOWN = 70;
const PHASE2_COOLDOWN_SCALE = 0.65;

// ── Types ─────────────────────────────────────────────────────────────────────
export type ComradeVexPhase = 1 | 2;

export type ComradeVexAttack = 'banner_sweep' | 'megaphone_stun';

export type ComradeVexNode =
  | 'scan'
  | 'approach'
  | 'banner_windup'
  | 'megaphone_windup'
  | 'summoning'
  | 'backstep';

export interface ComradeVexAIState {
  node: ComradeVexNode;
  phase: ComradeVexPhase;
  cooldown: number;
  windup: number;
  backstepFrames: number;
  summonFrames: number;
  /** Round-robin: 0 = banner_sweep, 1 = megaphone_stun. */
  attackCycle: number;
  /** True once the first cadre-agitator wave (66%) has been summoned. */
  firstWaveSummoned: boolean;
  /** True once the second cadre-agitator wave (33%) has been summoned. */
  secondWaveSummoned: boolean;
  /** Which wave is currently animating (0 = none). */
  pendingWave: 0 | 1 | 2;
}

export interface ComradeVexAIInput extends EnemyAIInput {
  /** Current HP ÷ maxHp (0..1). */
  hpFraction: number;
}

export interface ComradeVexAIOutput {
  moveX: number;
  moveY: number;
  facing: 1 | -1;
  wantsAttack: boolean;
  attackType: ComradeVexAttack | null;
  /** True on the single frame the adapter should spawn the first agitator wave (66% HP). */
  wantsSummonWave1: boolean;
  /** True on the single frame the adapter should spawn the second agitator wave (33% HP). */
  wantsSummonWave2: boolean;
  nextState: ComradeVexAIState;
}

export function initialComradeVexState(): ComradeVexAIState {
  return {
    node: 'scan',
    phase: 1,
    cooldown: 0,
    windup: 0,
    backstepFrames: 0,
    summonFrames: 0,
    attackCycle: 0,
    firstWaveSummoned: false,
    secondWaveSummoned: false,
    pendingWave: 0,
  };
}

/**
 * Advance the Comrade Vex behaviour tree by one frame.
 * Pure function — the Phaser adapter applies movement and spawns hitboxes /
 * agitator waves based on the output.
 */
export function tickComradeVexAI(
  input: ComradeVexAIInput,
  state: ComradeVexAIState,
): ComradeVexAIOutput {
  const facing: 1 | -1 = input.targetX >= input.selfX ? 1 : -1;

  if (input.isKO || input.isBeingThrown) {
    return noOp(facing, initialComradeVexState());
  }

  const phase: ComradeVexPhase = input.hpFraction <= PHASE_2_THRESHOLD ? 2 : 1;
  const { firstWaveSummoned, secondWaveSummoned } = state;

  // ── Summon gate: Wave 2 (33% HP) — checked first; marks wave 1 done too ───
  if (
    !secondWaveSummoned &&
    input.hpFraction <= SUMMON_WAVE_2_THRESHOLD &&
    state.node !== 'summoning'
  ) {
    return noOp(facing, {
      ...state,
      node: 'summoning',
      phase,
      firstWaveSummoned: true,
      secondWaveSummoned: true,
      pendingWave: 2,
      cooldown: 0,
      windup: 0,
      backstepFrames: 0,
      summonFrames: SUMMON_FRAMES,
    });
  }

  // ── Summon gate: Wave 1 (66% HP) ──────────────────────────────────────────
  if (
    !firstWaveSummoned &&
    input.hpFraction <= SUMMON_WAVE_1_THRESHOLD &&
    state.node !== 'summoning'
  ) {
    return noOp(facing, {
      ...state,
      node: 'summoning',
      phase,
      firstWaveSummoned: true,
      secondWaveSummoned,
      pendingWave: 1,
      cooldown: 0,
      windup: 0,
      backstepFrames: 0,
      summonFrames: SUMMON_FRAMES,
    });
  }

  // ── Tick counters ─────────────────────────────────────────────────────────
  const cooldown = Math.max(0, state.cooldown - 1);
  const windup = Math.max(0, state.windup - 1);
  const backstepFrames = Math.max(0, state.backstepFrames - 1);
  const summonFrames = Math.max(0, state.summonFrames - 1);

  // ── Hitstun: freeze animation, keep ticking cooldown ─────────────────────
  if (input.isHitstunned) {
    // If a hit interrupts the summon wind-up, re-arm the dedup flag for the
    // pending wave: the spawn signal only fires at wind-up completion, so without
    // this the agitator wave is silently never summoned. The HP gate re-fires it
    // on a later clean frame — still exactly once.
    const interrupted = state.node === 'summoning';
    return noOp(facing, {
      ...state,
      phase,
      firstWaveSummoned: interrupted && state.pendingWave === 1 ? false : firstWaveSummoned,
      secondWaveSummoned: interrupted && state.pendingWave === 2 ? false : secondWaveSummoned,
      node: 'scan',
      cooldown,
      windup: 0,
      backstepFrames: 0,
      summonFrames: 0,
    });
  }

  // ── SUMMONING ─────────────────────────────────────────────────────────────
  if (state.node === 'summoning') {
    if (summonFrames > 0) {
      return noOp(facing, {
        ...state,
        phase,
        firstWaveSummoned,
        secondWaveSummoned,
        cooldown,
        summonFrames,
      });
    }
    const wave = state.pendingWave;
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: false,
      attackType: null,
      wantsSummonWave1: wave === 1,
      wantsSummonWave2: wave === 2,
      nextState: {
        ...state,
        node: 'scan',
        phase,
        firstWaveSummoned,
        secondWaveSummoned,
        pendingWave: 0,
        cooldown: cooldownFor(input.aggressionScale, phase),
        summonFrames: 0,
      },
    };
  }

  // ── BACKSTEP ──────────────────────────────────────────────────────────────
  if (state.node === 'backstep') {
    if (backstepFrames > 0) {
      const { dx, dy, dist } = toTarget(input);
      const away = norm(-dx, -dy, dist);
      return {
        moveX: away.x,
        moveY: away.y,
        facing,
        wantsAttack: false,
        attackType: null,
        wantsSummonWave1: false,
        wantsSummonWave2: false,
        nextState: {
          ...state,
          phase,
          firstWaveSummoned,
          secondWaveSummoned,
          cooldown,
          backstepFrames,
        },
      };
    }
    return noOp(facing, {
      ...state,
      phase,
      firstWaveSummoned,
      secondWaveSummoned,
      cooldown,
      node: 'scan',
      backstepFrames: 0,
    });
  }

  // ── BANNER WINDUP ─────────────────────────────────────────────────────────
  if (state.node === 'banner_windup') {
    if (windup > 0) {
      return noOp(facing, {
        ...state,
        phase,
        firstWaveSummoned,
        secondWaveSummoned,
        cooldown,
        windup,
      });
    }
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'banner_sweep',
      wantsSummonWave1: false,
      wantsSummonWave2: false,
      nextState: {
        ...state,
        phase,
        firstWaveSummoned,
        secondWaveSummoned,
        node: 'backstep',
        cooldown: cooldownFor(input.aggressionScale, phase),
        windup: 0,
        backstepFrames: BACKSTEP_FRAMES,
        attackCycle: nextCycle(state.attackCycle),
      },
    };
  }

  // ── MEGAPHONE WINDUP ──────────────────────────────────────────────────────
  if (state.node === 'megaphone_windup') {
    if (windup > 0) {
      return noOp(facing, {
        ...state,
        phase,
        firstWaveSummoned,
        secondWaveSummoned,
        cooldown,
        windup,
      });
    }
    return {
      moveX: 0,
      moveY: 0,
      facing,
      wantsAttack: true,
      attackType: 'megaphone_stun',
      wantsSummonWave1: false,
      wantsSummonWave2: false,
      nextState: {
        ...state,
        phase,
        firstWaveSummoned,
        secondWaveSummoned,
        node: 'backstep',
        cooldown: cooldownFor(input.aggressionScale, phase),
        windup: 0,
        backstepFrames: BACKSTEP_FRAMES,
        attackCycle: nextCycle(state.attackCycle),
      },
    };
  }

  // ── SCAN / APPROACH → select next attack ──────────────────────────────────
  const { dist } = toTarget(input);
  const { attackCycle } = state;

  if (cooldown === 0 && input.canAttack) {
    if (attackCycle === 0) {
      // banner_sweep: requires being in close range
      if (dist <= BANNER_SWEEP_RANGE) {
        return noOp(facing, {
          ...state,
          phase,
          firstWaveSummoned,
          secondWaveSummoned,
          cooldown,
          node: 'banner_windup',
          windup: BANNER_WINDUP_FRAMES,
        });
      }
    } else {
      // megaphone_stun: fires at any range
      return noOp(facing, {
        ...state,
        phase,
        firstWaveSummoned,
        secondWaveSummoned,
        cooldown,
        node: 'megaphone_windup',
        windup: MEGAPHONE_WINDUP_FRAMES,
      });
    }
  }

  // ── Approach orbit position ───────────────────────────────────────────────
  const orbitX = input.targetX + input.orbitOffsetX;
  const orbitY = input.targetY + input.orbitOffsetY;
  const odx = orbitX - input.selfX;
  const ody = orbitY - input.selfY;
  const orbitDist = Math.hypot(odx, ody);

  if (orbitDist > 8) {
    const speedMul = 1 + input.aggressionScale * 0.3;
    const move = norm(odx, ody, orbitDist);
    const node: ComradeVexNode = dist > ENGAGE_RANGE ? 'scan' : 'approach';
    return {
      moveX: move.x * speedMul,
      moveY: move.y * speedMul,
      facing,
      wantsAttack: false,
      attackType: null,
      wantsSummonWave1: false,
      wantsSummonWave2: false,
      nextState: {
        ...state,
        phase,
        firstWaveSummoned,
        secondWaveSummoned,
        cooldown,
        node,
        windup: 0,
        backstepFrames: 0,
        summonFrames: 0,
      },
    };
  }

  return noOp(facing, {
    ...state,
    phase,
    firstWaveSummoned,
    secondWaveSummoned,
    cooldown,
    node: 'scan',
  });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function cooldownFor(aggressionScale: number, phase: ComradeVexPhase): number {
  const phaseScale = phase === 2 ? PHASE2_COOLDOWN_SCALE : 1.0;
  return Math.round(BASE_COOLDOWN * phaseScale * (1 - aggressionScale * 0.5));
}

function toTarget(input: ComradeVexAIInput): { dx: number; dy: number; dist: number } {
  const dx = input.targetX - input.selfX;
  const dy = input.targetY - input.selfY;
  return { dx, dy, dist: Math.hypot(dx, dy) };
}

function norm(x: number, y: number, len: number): { x: number; y: number } {
  const d = len || 1;
  return { x: x / d, y: y / d };
}

function noOp(facing: 1 | -1, nextState: ComradeVexAIState): ComradeVexAIOutput {
  return {
    moveX: 0,
    moveY: 0,
    facing,
    wantsAttack: false,
    attackType: null,
    wantsSummonWave1: false,
    wantsSummonWave2: false,
    nextState,
  };
}

function nextCycle(c: number): number {
  return (c + 1) % 2;
}
